import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import JobApplicationList from '../jobs/JobApplicationList';
import { useApiOperations } from '@utils/api-functions';
import { PlusCircle } from 'lucide-react';
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser"; // Import from correct package
import { ErrorBoundary } from '@shared/feedback/ErrorBoundary';
import monitoringService from '@services/monitoring';
import { debug } from '@utils/debug';

const RETRY_DELAY = 1000;
const MAX_RETRIES = 3;

const LoadingSpinner = ({ message }) => (
  <div className="flex justify-center items-center h-64">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-huntd-info"></div>
    <span className="ml-3 text-huntd-secondary">{message}</span>
  </div>
);

LoadingSpinner.propTypes = {
  message: PropTypes.string.isRequired
};

const DashboardView = () => {
  const { getJobApplications, updateJobApplication, getJobApplicationStats } = useApiOperations();
  const { accounts, inProgress } = useMsal();
  const isAuthenticated = accounts && accounts.length > 0; // Determine auth state
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [persistentError, setPersistentError] = useState(false); // New state for persistent errors
  const navigate = useNavigate();
  const location = useLocation();

  const showNewApplicationForm = location.pathname.includes('applications/new');

  const fetchDashboardData = useCallback(async (isManualRetry = false) => {
    // If it's not a manual retry and there's a persistent error, don't proceed
    if (!isManualRetry && persistentError) {
      debug('dashboard', 'Skipping fetch due to persistent error');
      return;
    }

    // Reset persistent error state if it's a manual retry attempt
    if (isManualRetry) {
      setPersistentError(false);
      setRetryCount(0); // Reset retry count for manual attempt
      setError(null); // Clear previous error message
    }

    const requestId = `dashboard-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
    const startTime = Date.now();

    monitoringService.trackEvent('DashboardDataFetch', {
      requestId,
      retry: retryCount > 0, // Indicate if it's an automatic retry
      retryCount,
      isManualRetry
    });
    if (inProgress !== InteractionStatus.None) { // Check if MSAL is busy
      debug('dashboard', 'Skipping fetch - auth loading');
      return;
    }

    if (!isAuthenticated) {
      const error = 'Authentication required. Please log in to view the dashboard.';
      debug('dashboard', 'Auth required', { error });
      monitoringService.trackEvent('DashboardAuthError', {
        requestId,
        error
      });
      setError(error);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null); // Clear previous errors on new attempt
      // Don't reset persistentError here, only on success or manual retry
      const [appsData, statsData] = await Promise.all([
        getJobApplications(),
        getJobApplicationStats()
      ]);

      monitoringService.trackEvent('DashboardDataLoaded', {
        requestId,
        duration: Date.now() - startTime,
        applicationsCount: appsData.length,
        hasStats: !!statsData
      });

      setApplications(appsData);
      setStats(statsData);
      setRetryCount(0); // Reset retry count on success
      setPersistentError(false); // Clear persistent error on success
    } catch (error) {
      debug('dashboard', 'Failed to fetch data', { error, retryCount, isManualRetry });
      console.error('Failed to fetch dashboard data:', error);

      monitoringService.trackException(error, {
        requestId,
        duration: Date.now() - startTime,
        operation: 'dashboard_fetch',
        retry: retryCount > 0,
        retryCount,
        isManualRetry
      });

      // Handle auth errors
      if (error.type === 'auth_required' || error.type === 'auth_failed') {
        const message = 'Authentication error. Please try logging in again.';
        setError(message);
        monitoringService.trackEvent('DashboardAuthError', {
          requestId,
          error: message,
          type: error.type
        });
        setPersistentError(true); // Auth errors are persistent until re-login
        return;
      }

      // Handle ONLY network errors with retry
      if (error.type === 'network_error' && retryCount < MAX_RETRIES) {
        const nextRetry = retryCount + 1;
        const delay = RETRY_DELAY * Math.pow(2, retryCount); // Exponential backoff

        debug('dashboard', 'Scheduling network error retry', { nextRetry, delay });
        monitoringService.trackEvent('DashboardRetryScheduled', {
          requestId,
          errorType: error.type,
          nextRetry,
          delay
        });

        setRetryCount(nextRetry);
        // Use a flag to indicate this is an automatic retry, not manual
        setTimeout(() => fetchDashboardData(false), delay);
        // Don't set persistent error yet, still retrying
        return; // Exit the catch block to allow retry
      }

      // If retries exhausted for network error, or any other non-auth error occurred
      debug('dashboard', 'Setting persistent error', { errorType: error.type, retryCount });
      setPersistentError(true); // Mark error as persistent

      // Set appropriate error message based on whether retries were attempted
      const message = (error.type === 'network_error' && retryCount >= MAX_RETRIES)
        ? 'Failed to load dashboard data after multiple network attempts. Please check your connection and try again.'
        : `Failed to load dashboard data: ${error.message || 'An unknown error occurred.'}`;
      setError(message);
      // Persistent error is already set above
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, inProgress, getJobApplications, getJobApplicationStats, retryCount, persistentError]); // Add persistentError dependency

  // Load data when auth state changes
  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      // Corrected check: Use inProgress instead of authLoading
      if (!mounted || inProgress !== InteractionStatus.None) return;
      // Fetch only if auth idle, authenticated, AND no persistent error exists
      if (isAuthenticated && inProgress === InteractionStatus.None && !persistentError) {
        await fetchDashboardData(false); // Pass false for isManualRetry
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, [fetchDashboardData, isAuthenticated, inProgress, persistentError]); // Add persistentError dependency

  // Handle form submission refresh
  useEffect(() => {
    if (location.state?.refresh) {
      fetchDashboardData(true); // Treat refresh as a manual retry
      // Clear the state to prevent unnecessary refreshes
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.refresh, navigate, location.pathname, fetchDashboardData]);

  const handleStatusChange = useCallback(async (applicationId, newStatus) => {
    if (!isAuthenticated) {
      setError('Authentication required to update status');
      return;
    }

    try {
      setError(null);
      await updateJobApplication(applicationId, {
        status: newStatus
      });
      // Refresh the applications list
      await fetchDashboardData(true); // Treat refresh as a manual retry
    } catch (error) {
      console.error('Failed to update application:', error);

      if (error.type === 'auth_required' || error.type === 'auth_failed') {
        setError('Authentication error. Please try logging in again.');
      } else if (error.type === 'network_error') {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError('Failed to update application status');
      }
    }
  }, [isAuthenticated, updateJobApplication, fetchDashboardData]); // fetchDashboardData dependency is correct

  const handleCreateNew = useCallback(() => {
    if (!isAuthenticated) {
      setError('Authentication required to create new application');
      return;
    }
    navigate('/app/dashboard/applications/new');
  }, [isAuthenticated, navigate]);

  const handleCloseForm = useCallback(() => {
    navigate('/app/dashboard');
  }, [navigate]);

  // Corrected check: Use inProgress instead of authLoading
  if (inProgress !== InteractionStatus.None || loading) {
    return (
      <LoadingSpinner
        // Corrected message check: Use inProgress instead of authLoading
        message={inProgress !== InteractionStatus.None ? 'Checking authentication...' : 'Loading dashboard...'}
      />
    );
  }

  // Render error state, differentiating between transient and persistent errors
  if (error) {
    const isPersistent = persistentError;
    const errorMessage = isPersistent
      ? `Persistent Error: ${error}. Please try again manually or contact support if the issue continues.`
      : error;

    return (
      <div className="bg-huntd-error/10 border border-huntd-error/20 text-huntd-error px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">{isPersistent ? 'Persistent Error!' : 'Error!'} </strong>
        <span className="block sm:inline">{errorMessage}</span>
        {/* Show Try Again button only if it's a persistent error or a network error */}
        {(isPersistent || error.includes('Network error')) && (
          <button
            onClick={() => fetchDashboardData(true)} // Pass true for manual retry
            className="mt-2 text-sm text-huntd-info hover:text-huntd-info/80 underline"
          >
            Try Again Manually
          </button>
        )}
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="container mx-auto px-4 py-8 relative">
      {/* Modal for New Application Form */}
      {showNewApplicationForm && (
        <div className="fixed inset-0 bg-huntd-dark bg-opacity-50 z-40 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <button
                onClick={handleCloseForm}
                className="absolute top-4 right-4 text-huntd-secondary hover:text-huntd-primary"
              >
                ×
              </button>
              <Outlet />
            </div>
          </div>
        </div>
      )}
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-huntd-primary">Job Application Dashboard</h1>
        <button
          onClick={handleCreateNew}
          className="inline-flex items-center px-4 py-2 bg-huntd-info text-white rounded-md hover:bg-huntd-info/80 focus:outline-none focus:ring-2 focus:ring-huntd-info focus:ring-offset-2"
        >
          <PlusCircle className="w-5 h-5 mr-2" />
          Create New Application
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-huntd-primary mb-2">Active Applications</h3>
          <p className="text-3xl font-bold text-huntd-info">
            {stats?.statusBreakdown.find(s => !['Rejected', 'Offer'].includes(s.status))?.count || 0}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-huntd-primary mb-2">Interviews Scheduled</h3>
          <p className="text-3xl font-bold text-huntd-warning">
            {stats?.statusBreakdown.find(s => s.status === 'Interviewing')?.count || 0}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-huntd-primary mb-2">Offers Received</h3>
          <p className="text-3xl font-bold text-huntd-success">
            {stats?.statusBreakdown.find(s => s.status === 'Offer')?.count || 0}
          </p>
        </div>
      </div>

      <JobApplicationList
        applications={applications}
        onStatusChange={handleStatusChange}
      />
      </div>
    </ErrorBoundary>
  );
};

export default DashboardView;
